
import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { Scene } from './components/Scene';
import { UI } from './components/UI';
import { Cursor } from './components/Cursor';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export type Section = 'HERO' | 'WORKS' | 'SOLUTIONS' | 'STUDIO' | 'EXPERTISE' | 'CONTACT';

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Fixed ErrorBoundary: Added constructor to explicitly pass props to super and initialize state.
// This ensures that the 'props' property from React.Component is correctly recognized by the TypeScript compiler.
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: any): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Three.js Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

const App: React.FC = () => {
  const [currentProject, setCurrentProject] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<Section>('HERO');
  
  // Refs for precise section tracking
  const heroRef = useRef<HTMLDivElement>(null);
  const worksRef = useRef<HTMLDivElement>(null);
  const solutionsRef = useRef<HTMLDivElement>(null);
  const studioRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Sync 3D progress with native scroll
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, scrollY / maxScroll));
      setScrollProgress(progress);

      // DOM-based active section detection
      // Using a buffer (40% of viewport) to trigger active state before the section hits the very top
      const buffer = window.innerHeight * 0.4;
      const currentPos = scrollY + buffer;

      if (contactRef.current && currentPos >= contactRef.current.offsetTop) {
        setActiveSection('CONTACT');
      } else if (studioRef.current && currentPos >= studioRef.current.offsetTop) {
        setActiveSection('STUDIO');
      } else if (solutionsRef.current && currentPos >= solutionsRef.current.offsetTop) {
        setActiveSection('SOLUTIONS');
      } else if (worksRef.current && currentPos >= worksRef.current.offsetTop) {
        setActiveSection('WORKS');
      } else {
        setActiveSection('HERO');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigate = (section: Section) => {
    // Legacy support if needed, but UI now handles this directly via Refs
    const waypoints = { HERO: 0, WORKS: 0.05, SOLUTIONS: 0.88, STUDIO: 0.94, EXPERTISE: 0.96, CONTACT: 1 };
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({
      top: waypoints[section] * maxScroll,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative w-full bg-[#050505] selection:bg-[#4444ff] selection:text-white">
      {/* Fixed 3D World Background */}
      <div className="fixed inset-0 w-full h-screen z-0">
        <ErrorBoundary fallback={<div className="absolute inset-0 flex items-center justify-center text-white/20 font-mono text-xs">3D CONTEXT ERROR</div>}>
          <Suspense fallback={null}>
            <Canvas
              shadows
              camera={{ position: [0, 0, 15], fov: 35 }}
              dpr={[1, 2]}
              gl={{ antialias: true, alpha: true }}
            >
              <Scene 
                onProjectChange={setCurrentProject} 
                progress={scrollProgress}
                activeSection={activeSection}
              />
            </Canvas>
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* Actual Scrollable Content */}
      <div ref={scrollContainerRef} className="relative z-20 w-full">
        <UI 
          currentProject={currentProject} 
          activeSection={activeSection} 
          progress={scrollProgress}
          onNavigate={handleNavigate}
          heroRef={heroRef}
          worksRef={worksRef}
          solutionsRef={solutionsRef}
          studioRef={studioRef}
          contactRef={contactRef}
        />
      </div>

      <Cursor />
      
      {/* Global Cinematic Effects */}
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_50%,rgba(20,20,20,0)_0%,rgba(5,5,5,1)_100%)] z-10" />
      <div className="fixed inset-0 pointer-events-none mix-blend-overlay opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] z-50" />
    </div>
  );
};

export default App;
